import { Link } from "@/components/LLink";
import { ArrowUpRight, Clock, MapPin, MessageCircle, Phone } from "lucide-react";
import { CONTACT } from "@/data/site";
import { useLang, useSite } from "@/i18n";
import { COUNTRY } from "@/seo/countryContent";

const ColTitle = ({ children }) => (
  <h4 className="mb-5 text-[11px] font-bold uppercase tracking-[0.3em] text-ink">{children}</h4>
);

const ColLink = ({ to, testid, children }) => (
  <li>
    <Link to={to} data-testid={testid} className="text-[15px] text-ink/60 transition-colors duration-300 hover:text-gold">
      {children}
    </Link>
  </li>
);

const SocialBtn = ({ href, label, testid, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    data-testid={testid}
    className="flex h-11 w-11 items-center justify-center rounded-full bg-base text-ink/70 shadow-sm ring-1 ring-ink/5 transition-[transform,color,box-shadow] duration-300 hover:-translate-y-0.5 hover:text-gold hover:shadow-md"
  >
    {children}
  </a>
);

function Brand() {
  const { t } = useLang();
  return (
    <div className="relative flex flex-col items-start lg:pr-16">
      <span
        aria-hidden
        className="pointer-events-none absolute -left-24 -top-24 h-[420px] w-[420px] rounded-full border-[48px] border-gold/[0.06]"
      />
      <img src="/logo.png" alt="HEAVY METAL" className="relative h-40 w-auto lg:h-48" data-testid="footer-logo" />
      <p className="relative mt-8 max-w-xs text-sm leading-relaxed text-ink/55">{t("footer_blurb")}</p>
      <div className="relative mt-8 flex items-center gap-4">
        <span className="text-sm text-ink/60">{t("follow")}</span>
        <SocialBtn href={CONTACT.whatsapp} label="WhatsApp" testid="footer-social-whatsapp">
          <MessageCircle className="h-5 w-5" />
        </SocialBtn>
        <SocialBtn href={CONTACT.mapsUrl.replace("&output=embed", "")} label="Google Maps" testid="footer-social-maps">
          <MapPin className="h-5 w-5" />
        </SocialBtn>
      </div>
    </div>
  );
}

function LinkColumns() {
  const { t, lang } = useLang();
  const { services, products } = useSite();
  const NAV_SERVICES = services.map((s) => ({ name: s.name, href: `/servicii/${s.slug}` }));
  const NAV_PRODUCTS = products.map((p) => ({ name: p.name, href: `/produse/${p.slug}` }));
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-4">
      <div>
        <ColTitle>{t("footer_services")}</ColTitle>
        <ul className="space-y-3">
          <ColLink to="/servicii" testid="footer-servicii">{t("hdr_all_services")}</ColLink>
          {NAV_SERVICES.map((s) => (
            <ColLink key={s.href} to={s.href} testid={`footer${s.href.replaceAll("/", "-")}`}>{s.name}</ColLink>
          ))}
        </ul>
      </div>
      <div>
        <ColTitle>{t("footer_products")}</ColTitle>
        <ul className="space-y-3">
          {NAV_PRODUCTS.map((p) => (
            <ColLink key={p.href} to={p.href} testid={`footer${p.href.replaceAll("/", "-")}`}>{p.name}</ColLink>
          ))}
        </ul>
      </div>
      <div>
        <ColTitle>{t("footer_company")}</ColTitle>
        <ul className="space-y-3">
          <ColLink to="/productie" testid="footer-productie">{t("nav_production")}</ColLink>
          <ColLink to="/portofoliu" testid="footer-portofoliu">{t("nav_portfolio")}</ColLink>
          <ColLink to="/b2b" testid="footer-b2b">B2B</ColLink>
          <ColLink to="/arhitecti" testid="footer-arhitecti">{t("b2b_link2").replace(" ↗", "")}</ColLink>
          <ColLink to="/export" testid="footer-export">{t("nav_export")}</ColLink>
          {lang === "ro" && <ColLink to="/confectii-metalice" testid="footer-zone">Zone deservite</ColLink>}
          {Object.entries(COUNTRY).filter(([, k]) => k.lang === lang).map(([slug, k]) => (
            <ColLink key={slug} to={`/${slug}`} testid={`footer-country-${slug}`}>{k.kicker}</ColLink>
          ))}
          <ColLink to="/contact" testid="footer-contact">{t("nav_contact")}</ColLink>
        </ul>
      </div>
      <div>
        <ColTitle>Legal</ColTitle>
        <ul className="space-y-3">
          <ColLink to="/termeni-conditii" testid="footer-termeni">{t("footer_terms")}</ColLink>
          <ColLink to="/politica-cookies" testid="footer-cookies">{t("footer_cookies")}</ColLink>
          <ColLink to="/anpc" testid="footer-anpc">ANPC</ColLink>
        </ul>
      </div>
    </div>
  );
}

function ContactRow() {
  const { t } = useLang();
  return (
    <div className="mt-16">
      <ColTitle>{t("footer_contact")}</ColTitle>
      <div className="space-y-3 text-[15px] text-ink/80" data-testid="footer-contact-row">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <a href={CONTACT.mapsUrl.replace("&output=embed", "")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 transition-colors hover:text-gold" data-testid="footer-address">
            <MapPin className="h-4 w-4 shrink-0 text-gold" />
            {CONTACT.address}
          </a>
          <span className="hidden h-1 w-1 rounded-full bg-ink/40 sm:block" aria-hidden />
          <a href={CONTACT.phoneHref} className="flex items-center gap-2.5 transition-colors hover:text-gold" data-testid="footer-phone">
            <Phone className="h-4 w-4 shrink-0 text-gold" />
            {CONTACT.phone}
          </a>
        </div>
        <div className="flex items-center gap-2.5" data-testid="footer-hours">
          <Clock className="h-4 w-4 shrink-0 text-gold" />
          <span>{CONTACT.hours.map((h, i) => `${t("days")[i]} ${i === 2 ? t("closed") : h.time}`).join(" · ")}</span>
        </div>
      </div>
    </div>
  );
}

export default function Footer() {
  const { t } = useLang();

  return (
    <footer className="border-t border-ink/10" data-testid="footer">
      <div className="relative overflow-hidden bg-surface">
        <div className="mx-auto grid max-w-7xl gap-16 px-4 py-20 sm:px-6 lg:grid-cols-[minmax(300px,1fr)_2fr] lg:px-8 lg:py-24">
          <Brand />
          <div>
            <LinkColumns />
            <ContactRow />
          </div>
        </div>
        <Link
          to="/contact"
          data-testid="footer-cta"
          className="absolute bottom-6 right-6 hidden items-center gap-2 rounded-lg bg-ink px-6 py-3.5 text-sm font-semibold text-base shadow-lg shadow-black/15 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold lg:flex"
        >
          {t("nav_cta")}
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>

      <div className="border-t border-ink/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-2 px-4 py-6 text-center text-xs text-ink/50 sm:flex-row sm:gap-6 sm:px-6 lg:px-8">
          <p data-testid="footer-copyright">Copyright © {new Date().getFullYear()} Heavy Metal — {t("footer_rights")}</p>
          <p>
            Proiect realizat de{" "}
            <a
              href="https://digitalagencyngr.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-credit"
              className="font-semibold text-ink/70 underline decoration-gold underline-offset-4 transition-colors hover:text-gold"
            >
              DIGITAL AGENCY NGR
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
